/**
 * Runtime configuration resolved from Vite environment variables.
 *
 * Centralising env access here keeps `import.meta.env` out of feature code and
 * provides a single, typed, validated source of truth. Import `env` anywhere;
 * never read `import.meta.env` directly outside this module.
 */

interface AppEnv {
  /** Base URL of the orchestration backend API, without a trailing slash. */
  readonly apiBaseUrl: string
  /** Path (relative to `apiBaseUrl`) of the AI streaming endpoint. */
  readonly aiStreamPath: string
  /** When true, stream from the built-in mock generator instead of the backend. */
  readonly useMockAi: boolean
  /** Human-readable application name. */
  readonly appName: string
  /** Convenience flag derived from Vite's mode. */
  readonly isProduction: boolean
}

function parseBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined) return fallback
  return value === 'true' || value === '1'
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url
}

function resolveEnv(): AppEnv {
  const apiBaseUrl = stripTrailingSlash(import.meta.env.VITE_API_BASE_URL ?? '/api')

  return {
    apiBaseUrl,
    aiStreamPath: import.meta.env.VITE_AI_STREAM_PATH ?? '/ai/stream',
    // Default to the mock when no backend URL is configured so the app is
    // runnable out of the box without a running server.
    useMockAi: parseBoolean(
      import.meta.env.VITE_USE_MOCK_AI,
      !import.meta.env.VITE_API_BASE_URL,
    ),
    appName: import.meta.env.VITE_APP_NAME ?? 'Weather Orchestra',
    isProduction: import.meta.env.PROD,
  }
}

export const env: AppEnv = resolveEnv()
